<template>
  <div
    v-if="eventStartFormattedDay && eventStartFormattedMonth && !singleDate"
    :class="{ big: big }"
    class="event-date-overlay"
  >
    <div class="start">
      <div class="day">
        {{ eventStartFormattedDay }}
      </div>
      <div class="month">
        {{ eventStartFormattedMonth }}
      </div>
    </div>
    <div
      v-if="eventEndFormatted && !(eventIsSameDay || eventIsAllDay)"
      class="end"
    >
      <div class="day">
        {{ eventEndFormattedDay }}
      </div>
      <div class="month">
        {{ eventEndFormattedMonth }}
      </div>
    </div>
  </div>
  <div
    v-else-if="eventStartFormattedDay && eventStartFormattedMonth"
    :class="{ big: big }"
    class="event-date-overlay"
  >
    <div :class="singleEventClass">
      <div v-if="!eventIsStarted" class="ends">Ends</div>
      <div class="day">
        {{ eventSingleFormattedDay }}
      </div>
      <div class="month">
        {{ eventSingleFormattedMonth }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { EventsStoryblok } from '~/types/storyblok'

const props = defineProps<{
  blok: EventsStoryblok
  big?: boolean
  singleDate?: boolean
}>()
const {
  eventStartFormattedDay,
  eventStartFormattedMonth,
  eventEndFormatted,
  eventEndFormattedDay,
  eventEndFormattedMonth,
  eventIsAllDay,
  singleEventClass,
  eventSingleFormattedDay,
  eventSingleFormattedMonth,
  eventIsSameDay,
  eventIsStarted,
} = useEvent(toRefs(props))
</script>

<style lang="less" scoped>
// Event date, shared between Event Card and Search Card
.event-date-overlay {
  .flex();
  .absolute();
  .font-headings();
  .text-center();

  top: -@4px;
  left: @gutter-xxs;

  .start,
  .end {
    .relative();

    margin: 0;
    padding: @3px @7px;
    color: @color-text;
    background: @color-fill-normal;
    border-top: @4px solid @color-tertiary;
    box-shadow: 0 @1px @1px 0 rgba(0, 0, 0, 0.15);

    @media @desktop {
      padding: @gutter-xxs;
      box-shadow: 0 @1px @1px 0 rgba(0, 0, 0, 0.2);
    }

    .day {
      .font-size(18, 21);
      .font-black();

      @media @desktop {
        .font-size(30, 33);
      }
    }

    .month {
      .uppercase();
      .font-bold();
      .font-size(11, 12);

      @media @desktop {
        .font-size(14, 15);
      }
    }

    .ends {
      .uppercase();
      .font-size(9, 12);
    }
  }

  .end {
    border-top-color: @color-primary;
    margin-left: @1px;

    &::before {
      .absolute();

      content: '';
      height: 100%;
      width: @1px;
      top: 0;
      left: -@1px;
      background: @color-fill-pale;
    }
  }

  &.big {
    .font-headings();

    top: @gutter-sm;
    left: @gutter-sm;
    padding: 0;

    .start,
    .end {
      border-top-width: @6px;
      padding: @gutter-xxs;

      @media @desktop {
        box-shadow: 0 @2px @2px 0 rgba(0, 0, 0, 0.15);
      }

      .day {
        .font-black();
        .font-size(66, 63);
      }

      .month {
        .font-size(14, 21);
        .space(0.05);
      }
    }
    .end {
      margin-left: @2px;
    }
  }
}
</style>
